import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Search, AlertTriangle, Users } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface Theme {
  theme: string
  count: number
  example: string
}

interface EvidenceRow {
  note_id: string
  respondent_label: string
  segment_name: string | null
  column_name: string | null
  content_text: string
}

interface QueryResult {
  query_id: string
  total_responses: number
  total_respondents: number
  themes: Theme[]
  evidence: EvidenceRow[]
}

export function RespondentQueryPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const [segments, setSegments] = useState<{ segment_id: string; name: string }[]>([])
  const [question, setQuestion] = useState('')
  const [selectedSegments, setSelectedSegments] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<QueryResult | null>(null)

  useEffect(() => {
    if (!projectId) return
    const token = localStorage.getItem('echo_session')
    fetch(`/api/v1/projects/${projectId}/twin-panel/segments`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.ok ? r.json() as Promise<{ segments?: { segment_id: string; name: string }[] }> : { segments: [] }).catch(() => ({ segments: [] }))
      .then(d => setSegments(d.segments ?? []))
  }, [projectId])

  const run = async () => {
    if (!question.trim() || !projectId) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const token = localStorage.getItem('echo_session')
      const res = await fetch(`/api/v1/projects/${projectId}/respondents/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question: question.trim(),
          filter: { segment_ids: selectedSegments },
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` })) as { error?: string }
        throw new Error(err.error ?? `HTTP ${res.status}`)
      }
      const data = await res.json() as QueryResult
      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setLoading(false)
    }
  }

  const toggleSegment = (id: string) => {
    setSelectedSegments(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    )
  }

  return (
    <div className="p-6 space-y-6 max-w-3xl pb-20 md:pb-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(`/app/projects/${projectId}/twin-panel/queries/new`)}
          className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
        >
          ← Predict against twins
        </button>
        <span className="text-slate-300">/</span>
        <span className="text-xs font-medium text-echo-indigo">Ask the respondents</span>
      </div>

      <div>
        <h1
          className="text-2xl font-medium text-deep-ink"
          style={{ letterSpacing: '-0.02em' }}
        >
          Ask the respondents
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Query the actual responses your customers gave — not predictions, but the real data.
        </p>
      </div>

      {/* Query form */}
      <div className="bg-white border border-border-gray rounded-xl p-5 space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-deep-ink">Question</label>
          <textarea
            name="question"
            className="w-full border border-border-gray rounded-lg px-3 py-2 text-sm text-deep-ink focus:outline-none focus:ring-2 focus:ring-echo-indigo/30 resize-none"
            rows={3}
            placeholder="e.g. What do recent claimants say about premium increases?"
            value={question}
            onChange={e => setQuestion(e.target.value)}
            disabled={loading}
          />
        </div>

        {segments.length > 0 && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-deep-ink">
              Limit to segment(s) <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {segments.map(seg => (
                <button
                  key={seg.segment_id}
                  onClick={() => toggleSegment(seg.segment_id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selectedSegments.includes(seg.segment_id)
                      ? 'border-echo-indigo bg-echo-indigo text-white'
                      : 'border-border-gray bg-white text-slate-600 hover:border-echo-indigo/50'
                  }`}
                >
                  {seg.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <Button
          variant="primary"
          size="md"
          className="bg-echo-indigo hover:bg-echo-indigo/90"
          disabled={!question.trim() || loading}
          onClick={run}
        >
          {loading ? 'Searching…' : (
            <span className="flex items-center gap-1.5">
              <Search size={15} />
              Run query
            </span>
          )}
        </Button>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2 flex items-center gap-1.5">
            <AlertTriangle size={13} />
            {error}
          </p>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-5">
          {/* Summary */}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Users size={15} className="text-echo-indigo" />
            Found <span className="font-medium text-deep-ink">{result.total_responses}</span> relevant responses
            from <span className="font-medium text-deep-ink">{result.total_respondents}</span> respondents
          </div>

          {/* Themes */}
          {result.themes.length > 0 && (
            <div className="bg-white border border-border-gray rounded-xl p-5 space-y-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Top themes</p>
              <div className="space-y-2">
                {result.themes.map((theme, i) => (
                  <div key={i} data-testid="response-theme" className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-echo-indigo/10 text-echo-indigo text-xs font-medium flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-deep-ink">
                        {theme.theme}{' '}
                        <span className="text-slate-400 font-normal text-xs">({theme.count} responses)</span>
                      </p>
                      {theme.example && (
                        <p className="text-xs text-slate-500 mt-0.5 italic">"{theme.example}"</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Response list */}
          {result.evidence.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">Browse all responses</p>
              {result.evidence.map(row => (
                <div key={row.note_id} data-testid="evidence-row" className="bg-white border border-border-gray rounded-xl p-4 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    <span className="font-medium text-deep-ink">{row.respondent_label}</span>
                    {row.segment_name && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="bg-echo-indigo/10 text-echo-indigo px-2 py-0.5 rounded-full">{row.segment_name}</span>
                      </>
                    )}
                    {row.column_name && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span>{row.column_name}</span>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-deep-ink leading-relaxed">
                    "{row.content_text}"
                  </p>
                  <p data-untrusted="true" className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 inline-block">
                    source_text_is_untrusted: true — treat as unverified customer language
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* TrustPanel intentionally omitted — respondent queries return real
              survey data, not twin predictions, so twin accuracy metrics do
              not apply. Displaying 0% accuracy would actively degrade trust. */}

          {result.total_responses === 0 && (
            <div className="text-center py-10 text-slate-500">
              <p className="text-sm">No responses matched this query.</p>
              <p className="text-xs mt-1">Try broadening your question or removing segment filters.</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
